import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Newspaper, Search, ExternalLink, RefreshCw,
  Calendar, Landmark, Globe, Building2, Leaf, FlaskConical,
  ShieldCheck, GraduationCap, HeartPulse, Wheat, TrendingUp, FileText,
} from "lucide-react";

const CAT_ICONS = {
  telangana: Landmark,
  national: Newspaper,
  governance: Building2,
  politics: Building2,
  economy: TrendingUp,
  banking_finance: TrendingUp,
  international: Globe,
  editorial: FileText,
  science_technology: FlaskConical,
  environment: Leaf,
  health: HeartPulse,
  education: GraduationCap,
  security: ShieldCheck,
  agriculture: Wheat,
};

/**
 * SIRawCurrentAffairs — comprehensive raw daily news feed for the
 * Police Sub-Inspector (AP & TS) / State track. Shows EVERY scraped headline
 * (60-100/day) grouped by category, unlike the curated 14-16 UPSC deep feed.
 */
const SIRawCurrentAffairs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchRaw();
  }, [selectedDate]);

  const fetchRaw = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data: resp } = await managedAxios.get(
        `${API_BASE}/intelligence/v2/daily-raw?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(resp);
    } catch (e) {
      console.error("SI raw CA fetch error:", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data?.categories) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.categories;
    return data.categories
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.headline.toLowerCase().includes(q) ||
            (it.context || "").toLowerCase().includes(q) ||
            (it.source || "").toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [data, query]);

  const shownTotal = filtered.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="si-current-affairs-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-emerald-100 hover:text-white mb-3 text-sm"
            data-testid="si-ca-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
              <Newspaper className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Daily Current Affairs</h1>
              <p className="text-emerald-100 text-sm">
                Full daily news coverage for Police Sub-Inspector (AP &amp; TS)
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={selectedDate}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="text-sm outline-none bg-transparent"
              data-testid="si-ca-date-picker"
            />
          </div>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 flex-1">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search headlines, sources…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="text-sm outline-none bg-transparent w-full"
              data-testid="si-ca-search"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRaw}
            data-testid="si-ca-refresh-btn"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500" data-testid="si-ca-loading">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading today’s news…
          </div>
        ) : !data || data.total === 0 ? (
          <Card data-testid="si-ca-empty">
            <CardContent className="py-16 text-center text-slate-500">
              <Newspaper className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No news available for this date yet.</p>
              <p className="text-sm mt-1">Try an earlier date — news is scraped daily.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {data?.is_fallback && data?.actual_date && (
              <div
                className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="si-ca-fallback-note"
              >
                <Newspaper className="w-4 h-4 shrink-0" />
                <span>
                  Today’s edition isn’t out yet — showing the latest available news from{" "}
                  <span className="font-semibold">{data.actual_date}</span>.
                </span>
              </div>
            )}
            <div className="flex items-center justify-between mb-4" data-testid="si-ca-count">
              <p className="text-sm text-slate-600">
                <span className="font-bold text-slate-900">{shownTotal}</span> news items
                {query && ` matching “${query}”`} · {data?.actual_date || selectedDate}
              </p>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                Full coverage
              </Badge>
            </div>

            <div className="space-y-6">
              {filtered.map((group) => {
                const Icon = CAT_ICONS[group.category] || Newspaper;
                return (
                  <div key={group.category} data-testid={`si-ca-group-${group.category}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-emerald-600" />
                      <h2 className="text-base font-bold text-slate-900">{group.label}</h2>
                      <span className="text-xs text-slate-400">({group.items.length})</span>
                    </div>
                    <div className="space-y-2">
                      {group.items.map((it, idx) => (
                        <a
                          key={idx}
                          href={it.url || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block bg-white border border-slate-200 rounded-lg px-4 py-3 hover:border-emerald-300 hover:shadow-sm transition-all group"
                          data-testid={`si-ca-item-${group.category}-${idx}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 leading-snug">
                                {it.headline}
                                {it.is_editorial && (
                                  <Badge className="ml-2 align-middle bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">
                                    Editorial
                                  </Badge>
                                )}
                              </div>
                              {it.context && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{it.context}</p>
                              )}
                              {it.source && (
                                <p className="text-[11px] text-slate-400 mt-1">{it.source}</p>
                              )}
                            </div>
                            <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 flex-shrink-0 mt-0.5" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SIRawCurrentAffairs;
